/**
 * AffichesBuilder — Multi-step flow pour créer les affiches d'un panneau et générer un PDF recto/verso.
 * 
 * Étapes :
 * 1) Type de panneau (Déroulant / LED / Grand format)
 * 2) Sélection panneau/emplacement
 * 3) Upload des 10 (ou N) images (5 côté A + 5 côté B pour déroulants)
 * 4) Aperçu + Génération PDF
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Upload, FileDown, Trash2, CheckCircle2, Layers, Send, Loader2, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { PanelImage } from '../lib/panelImages';

const API = process.env.REACT_APP_BACKEND_URL;

const PANEL_TYPES = [
  { key: 'Panneau déroulant', label: 'Panneaux déroulants',   img: '🎞️', slots: 10, sides: 2, mediaType: 'image', accept: 'image/*',
    subtitle: '10 emplacements · 2 côtés × 5 affiches · PDF recto/verso' },
  { key: 'Panneau LED',       label: 'Panneaux LED',           img: '💡', slots: 10, sides: 1, mediaType: 'video', accept: 'video/*',
    subtitle: '3 emplacements · 10 vidéos · Face unique' },
  { key: 'Grand format',      label: 'Panneau grand format',   img: '🎬', slots: 10, sides: 1, mediaType: 'video', accept: 'video/*',
    subtitle: '1 emplacement · 10 vidéos · Face unique' },
  { key: 'Panneau poteau',    label: 'Panneaux poteaux',       img: '📍', slots: 25, sides: 1, mediaType: 'image', accept: 'image/*',
    subtitle: '4 avenues · 25 affiches par avenue · Face unique' },
];

// SVG mini-panneaux stylisés selon le type
const PanelMiniSvg = ({ type, selected = false }) => (
  <PanelImage type={type} className={`w-full h-full transition-transform duration-300 ${selected ? 'scale-105 drop-shadow-lg' : ''}`} />
);

const isPoteauKey = (k) => k === 'Panneau poteau';

// Nombre d'emplacements par type (correspond au module Emplacement)
const PANEL_COUNTS = {
  'Panneau déroulant': 10,
  'Panneau LED': 3,
  'Grand format': 1,
  'Panneau poteau': 4,
};

export default function AffichesBuilder({ open, onClose, emplacements = [] }) {
  const [step, setStep] = useState(1);
  const [panelType, setPanelType] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [coteA, setCoteA] = useState(Array(10).fill(null));
  const [coteB, setCoteB] = useState(Array(5).fill(null));
  const [uploading, setUploading] = useState({});
  const [previewSide, setPreviewSide] = useState('A');
  const [previewPage, setPreviewPage] = useState(0); // pour poteau (5 pages de 5)
  const [generating, setGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [sendingTech, setSendingTech] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [sendingTechnique, setSendingTechnique] = useState(false);
  const [techniqueSent, setTechniqueSent] = useState(null);
  const [savingCampaign, setSavingCampaign] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1); setPanelType(null); setSelectedEmp(null);
      setCoteA(Array(10).fill(null)); setCoteB(Array(5).fill(null));
      setShowSuccess(false); setPdfBlobUrl(null); setPreviewPage(0); setLastPayload(null); setSentTo(null); setTechniqueSent(null);
    }
  }, [open]);

  // Reset slots quand on change de type de panneau
  useEffect(() => {
    if (panelType) {
      const aSlots = panelType.sides === 2 ? 5 : panelType.slots;
      setCoteA(Array(aSlots).fill(null));
      setCoteB(Array(5).fill(null));
    }
  }, [panelType?.key]);

  // Emplacements filtrés par type — mêmes N emplacements officiels que Nos Panneaux / Emplacement
  const filteredEmps = (emplacements || [])
    .filter(e => e.type_panneau === panelType?.key)
    .sort((a, b) => ((Number(a.numero) || 999) - (Number(b.numero) || 999)) || String(a.created_at || '').localeCompare(String(b.created_at || '')))
    .slice(0, PANEL_COUNTS[panelType?.key] || 10);

  // Recharge les affiches déjà enregistrées pour ce panneau (ajout / retrait au fur et à mesure)
  useEffect(() => {
    if (!selectedEmp || !panelType) return;
    const toSlot = (path, i, names, companies) => (path ? { path, previewUrl: `${API}/api/affiches/media/${path}`, name: names?.[i] || '', company: companies?.[i] || '' } : null);
    axios.get(`${API}/api/affiches/campaign/${encodeURIComponent(selectedEmp.id || selectedEmp._id || selectedEmp.nom)}`, {
      params: { nom: selectedEmp.nom || '', type: panelType.key === 'Panneau LED' ? 'led' : panelType.key === 'Grand format' ? 'grand' : isPoteauKey(panelType.key) ? 'poteau' : 'roulant' },
    })
      .then(({ data: c }) => {
        const aSlots = panelType.sides === 2 ? 5 : panelType.slots;
        let a = (c.cote_a || []).map((p, i) => toSlot(p, i, c.cote_a_names, c.cote_a_companies));
        let b = (c.cote_b || []).map((p, i) => toSlot(p, i, c.cote_b_names, c.cote_b_companies));
        if (panelType.sides !== 2 && !isPoteauKey(panelType.key)) { a = [...a, ...b]; b = []; }
        setCoteA(Array.from({ length: aSlots }, (_, i) => a[i] || null));
        setCoteB(Array.from({ length: 5 }, (_, i) => b[i] || null));
        toast.info(`${[...a, ...b].filter(Boolean).length} ${panelType.mediaType === 'video' ? 'vidéo(s)' : 'affiche(s)'} déjà sur ce panneau`);
      })
      .catch(() => {
        setCoteA(Array(panelType.sides === 2 ? 5 : panelType.slots).fill(null));
        setCoteB(Array(5).fill(null));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmp?.id, panelType?.key]);

  const uploadImage = async (side, idx, file) => {
    const key = `${side}-${idx}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('slot', `${side}${idx + 1}`);
      const r = await axios.post(`${API}/api/affiches/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const path = r.data?.path;
      const previewUrl = URL.createObjectURL(file);
      if (side === 'A') {
        const next = [...coteA];
        next[idx] = { ...(next[idx] || {}), path, previewUrl, name: file.name };
        setCoteA(next);
      } else {
        const next = [...coteB];
        next[idx] = { ...(next[idx] || {}), path, previewUrl, name: file.name };
        setCoteB(next);
      }
      toast.success(`${panelType?.mediaType === 'video' ? 'Vidéo' : 'Image'} ${side}${idx + 1} chargée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const removeImage = (side, idx) => {
    if (side === 'A') { const next = [...coteA]; next[idx] = null; setCoteA(next); }
    else { const next = [...coteB]; next[idx] = null; setCoteB(next); }
  };

  const setCompany = (side, idx, company) => {
    if (side === 'A') {
      const next = [...coteA];
      next[idx] = { ...(next[idx] || { path: '', name: '' }), company };
      setCoteA(next);
    } else {
      const next = [...coteB];
      next[idx] = { ...(next[idx] || { path: '', name: '' }), company };
      setCoteB(next);
    }
  };

  const isPoteau = panelType?.key === 'Panneau poteau';
  const isDoubleSide = panelType?.sides === 2;

  const buildPayload = () => {
    // Poteau : 25 slots single-side · Déroulant : 5 A + 5 B · LED / Grand format : split A/B
    let a, b, totalSlots;
    if (isPoteau) {
      a = coteA.slice(0, 25); b = []; totalSlots = 25;
    } else if (isDoubleSide) {
      a = coteA.slice(0, 5); b = coteB.slice(0, 5); totalSlots = 10;
    } else {
      a = coteA.slice(0, 5); b = coteA.slice(5, 10); totalSlots = 10;
    }
    return {
      panneau_type: panelType.key,
      panneau_numero: String(selectedEmp?.numero || ''),
      emplacement_nom: selectedEmp?.nom || '',
      media_type: panelType.mediaType,
      is_double_side: isDoubleSide && !isPoteau,
      total_slots: totalSlots,
      cote_a: a.map(x => x?.path || ''),
      cote_a_names: a.map(x => x?.name || ''),
      cote_a_companies: a.map(x => x?.company || ''),
      cote_b: b.map(x => x?.path || ''),
      cote_b_names: b.map(x => x?.name || ''),
      cote_b_companies: b.map(x => x?.company || ''),
    };
  };

  const saveCampaign = async (silent = false) => {
    setSavingCampaign(true);
    try {
      await axios.post(`${API}/api/affiches/campaign`, { ...buildPayload(), panneau_id: selectedEmp?.id || selectedEmp?._id || '' });
      if (!silent) toast.success(`Enregistré : ${affichesRemplies} ${panelType?.mediaType === 'video' ? 'vidéo(s)' : 'affiche(s)'} sur « ${selectedEmp?.nom} » — visibles dans Nos Panneaux`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSavingCampaign(false); }
  };

  const sendToTechnique = async () => {
    setSendingTechnique(true);
    try {
      await saveCampaign(true);
      const r = await axios.post(`${API}/api/technique/envois-affiches`, buildPayload());
      setTechniqueSent(r.data?.techniciens || []);
      toast.success(`Images envoyées aux services techniques (${r.data?.sent || 0} technicien(s) notifié(s))`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi aux services techniques impossible');
    } finally { setSendingTechnique(false); }
  };

  const generatePdf = async () => {
    setGenerating(true);
    try {
      const payload = buildPayload();
      const r = await axios.post(`${API}/api/affiches/generate-pdf`, payload, { responseType: 'blob' });
      setLastPayload(payload);
      setSentTo(null);
      const contentType = r.data?.type || '';
      if (!contentType.includes('pdf')) {
        const txt = await r.data.text();
        throw new Error(txt || 'Réponse invalide du serveur');
      }
      const url = URL.createObjectURL(r.data);
      setPdfBlobUrl(url);
      setShowSuccess(true);

      // Sauvegarde la campagne pour l'afficher plus tard depuis "Nos Panneaux"
      try {
        await axios.post(`${API}/api/affiches/campaign`, {
          ...payload,
          panneau_id: selectedEmp?.id || selectedEmp?._id || '',
        });
      } catch (saveErr) {
        console.warn('[AffichesBuilder] campaign save failed', saveErr);
      }
    } catch (e) {
      // Extraire le message d'erreur, même quand responseType='blob'
      let msg = 'Erreur génération PDF';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { msg = JSON.parse(txt)?.detail || msg; } catch { msg = txt || msg; }
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        } else if (e?.message) {
          msg = e.message;
        }
      } catch {}
      console.error('[AffichesBuilder] generate-pdf error:', msg, e);
      toast.error(String(msg).slice(0, 240));
    } finally { setGenerating(false); }
  };

  const sendToTechniciens = async () => {
    if (!lastPayload) return;
    setSendingTech(true);
    try {
      const r = await axios.post(`${API}/api/affiches/send-to-techniciens`, lastPayload);
      setSentTo(r.data?.techniciens || []);
      toast.success(`Envoyé à ${r.data?.sent || 0} technicien(s) dans la messagerie`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi aux techniciens impossible");
    } finally { setSendingTech(false); }
  };

  const downloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl; a.download = `Panneau_${selectedEmp?.numero || 'X'}.pdf`; a.click();
  };

  // ─── Rendu ───
  if (!open) return null;

  // Step 4 = page de création
  const affichesTotal = panelType?.slots || 1;
  const affichesRemplies = (isDoubleSide
    ? coteA.slice(0, 5).filter(x => x?.path).length + coteB.slice(0, 5).filter(x => x?.path).length
    : coteA.slice(0, affichesTotal).filter(x => x?.path).length);
  const mediaLabel = panelType?.mediaType === 'video' ? 'vidéo' : (panelType?.mediaType === 'image' && panelType?.key === 'Panneau LED' ? 'image animée' : 'affiche');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-hidden p-0" data-testid="affiches-builder">
        <DialogHeader className="px-5 py-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Layers className="h-5 w-5" /> Création des affiches
            <span className="ml-3 text-xs font-normal opacity-85">Étape {step}/4</span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex bg-slate-50 border-b border-slate-200 px-5 py-2 gap-1">
          {['Type de panneau', 'Sélection emplacement', 'Ajout des affiches', 'Aperçu & PDF'].map((label, i) => (
            <div key={i} className={`flex-1 py-2 px-3 text-xs font-semibold text-center rounded ${
              step === i + 1 ? 'bg-blue-600 text-white' : step > i + 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
            }`}>
              {step > i + 1 && '✓ '}{i + 1}. {label}
            </div>
          ))}
        </div>

        <div className="overflow-y-auto max-h-[70vh] p-5">
          {/* ────────── ÉTAPE 1 — Type de panneau ────────── */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Choix du type de panneaux</h3>
              <p className="text-sm text-slate-500 mb-5">Sélectionnez le type pour lequel vous voulez créer vos affiches.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {PANEL_TYPES.map(t => {
                  const isSel = panelType?.key === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setPanelType(t)}
                      className={`p-5 rounded-xl border-2 transition-all text-left hover:shadow-lg ${
                        isSel ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`type-${t.key}`}
                    >
                      <div className="w-full h-28 flex items-center justify-center mb-3 bg-slate-50 rounded-lg">
                        <div className="w-full h-28 p-1"><PanelMiniSvg type={t.key} selected={isSel} /></div>
                      </div>
                      <div className={`font-bold ${isSel ? 'text-blue-900' : 'text-slate-900'}`}>{t.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{t.subtitle}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ────────── ÉTAPE 2 — Sélection panneau ────────── */}
          {step === 2 && panelType && (
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Sélection d'un panneau</h3>
              <p className="text-sm text-slate-500 mb-5">
                Cliquez sur un panneau pour créer ses affiches ({filteredEmps.length}/{PANEL_COUNTS[panelType.key]} disponible{filteredEmps.length > 1 ? 's' : ''}).
              </p>
              {filteredEmps.length === 0 ? (
                <div className="p-12 text-center text-sm text-slate-400 border border-dashed rounded-lg">
                  Aucun panneau de type "{panelType.label}" dans la base emplacements.
                </div>
              ) : (
                <div className={`grid gap-3 ${isPoteau ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-5 md:grid-cols-10'}`}>
                  {filteredEmps.map((emp, i) => {
                    const isSel = selectedEmp?.id === emp.id;
                    return (
                      <button
                        key={emp.id}
                        onClick={() => setSelectedEmp(emp)}
                        className={`p-2 rounded-lg border-2 transition text-center ${
                          isSel ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' : 'border-slate-200 bg-white hover:border-slate-400 hover:shadow'
                        }`}
                        data-testid={`emp-${emp.id}`}
                      >
                        <div className="text-lg font-extrabold text-slate-900">{i + 1}</div>
                        <div className={`${isPoteau ? 'w-16 h-24 mx-auto' : 'w-full h-20'} my-1`}>
                          <PanelMiniSvg type={panelType.key} selected={isSel} />
                        </div>
                        <div className={`${isPoteau ? 'text-sm font-bold' : 'text-[10px]'} text-slate-700 truncate`} title={emp.nom}>
                          {isPoteau ? emp.nom : `Emplacement ${i + 1}`}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {isPoteau ? `Capacité : ${emp.capacite_poteaux || 25} poteaux` : emp.nom}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ────────── ÉTAPE 3 — Upload images ────────── */}
          {step === 3 && panelType && selectedEmp && (
            <div>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Panneau {selectedEmp.numero || '?'}</h3>
                  <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                    <div><b>Type :</b> {panelType.label}</div>
                    <div><b>Emplacement :</b> {selectedEmp.nom}</div>
                    <div><b>Nombre de {mediaLabel}s :</b> {affichesTotal} {isDoubleSide && '(5 par côté)'}</div>
                    <div><b>Dimension :</b> 1,60 m (H) × 1,20 m (L)</div>
                    <div><b>Média :</b> {panelType.mediaType === 'video' ? 'Vidéos (mp4, mov, webm…)' : (panelType.key === 'Panneau LED' ? 'Images animées (GIF/JPEG/PNG)' : 'Images (JPEG/PNG)')}</div>
                  </div>
                </div>
                <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-xs">
                  <div className="font-bold text-blue-900 mb-1">ℹ️ Format de sortie</div>
                  <div className="text-slate-700 leading-relaxed">
                    {isPoteau ? (
                      <>Une affiche par poteau (25 poteaux/avenue).<br/>Export : <b>PDF planning · 5 pages × 5 poteaux</b>.</>
                    ) : isDoubleSide ? (
                      <>Chaque côté = 5 affiches superposées (8m de long).<br/>2 côtés = 10 affiches au total. Export : <b>PDF recto/verso</b>.</>
                    ) : (
                      <>Face unique = 10 {mediaLabel}s.<br/>Export : <b>PDF planning/storyboard 1 page</b>.</>
                    )}
                  </div>
                </div>
              </div>

              {isPoteau ? (
                <PoteauEditor
                  images={coteA}
                  uploading={uploading}
                  onUpload={(idx, file) => uploadImage('A', idx, file)}
                  onRemove={(idx) => removeImage('A', idx)}
                  onSetCompany={(idx, val) => setCompany('A', idx, val)}
                  avenueName={selectedEmp?.nom || ''}
                />
              ) : (
                <div className={`grid ${isDoubleSide ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                  <SideEditor
                    side="A"
                    label={isDoubleSide ? 'Côté A (Affiches 1 à 5)' : `${panelType.mediaType === 'video' ? 'Vidéos' : 'Images'} (1 à ${affichesTotal})`}
                    slots={isDoubleSide ? 5 : affichesTotal}
                    startNum={1}
                    images={coteA} uploading={uploading}
                    accept={panelType.accept}
                    mediaType={panelType.mediaType}
                    onUpload={(idx, file) => uploadImage('A', idx, file)}
                    onRemove={(idx) => removeImage('A', idx)}
                    onSetCompany={(idx, val) => setCompany('A', idx, val)}
                  />
                  {isDoubleSide && (
                    <SideEditor
                      side="B" label="Côté B (Affiches 6 à 10)"
                      slots={5} startNum={6}
                      images={coteB} uploading={uploading}
                      accept={panelType.accept}
                      mediaType={panelType.mediaType}
                      onUpload={(idx, file) => uploadImage('B', idx, file)}
                      onRemove={(idx) => removeImage('B', idx)}
                      onSetCompany={(idx, val) => setCompany('B', idx, val)}
                    />
                  )}
                </div>
              )}

              <div className="mt-4 text-xs text-slate-500 text-center">
                {affichesRemplies}/{affichesTotal} {isPoteau ? 'poteau' : mediaLabel}{affichesRemplies > 1 ? 's' : ''} ajoutée{affichesRemplies > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* ────────── ÉTAPE 4 — Aperçu + PDF ────────── */}
          {step === 4 && panelType && selectedEmp && (
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">
                {isPoteau ? 'Aperçu de la tournée poteaux' : panelType.mediaType === 'video' ? 'Aperçu des vidéos (ordre de diffusion)' : 'Aperçu des affiches (superposées verticalement)'}
              </h3>
              <p className="text-sm text-slate-500 mb-5">
                {isPoteau
                  ? `Vérifiez les 25 affiches liées aux poteaux de « ${selectedEmp?.nom} », puis générez le PDF planning.`
                  : 'Vérifiez le rendu, puis générez le PDF recto/verso.'}
              </p>

              {isPoteau ? (
                <PoteauPreview images={coteA} avenueName={selectedEmp?.nom || ''} />
              ) : (
                <>
                  {isDoubleSide && (
                    <div className="flex gap-2 mb-4 justify-center">
                      <button onClick={() => setPreviewSide('A')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${previewSide === 'A' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                        Côté A (Affiches 1 à 5)
                      </button>
                      <button onClick={() => setPreviewSide('B')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${previewSide === 'B' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                        Côté B (Affiches 6 à 10)
                      </button>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <SidePreview
                      images={isDoubleSide ? (previewSide === 'A' ? coteA : coteB) : coteA.slice(0, affichesTotal)}
                      startNum={isDoubleSide && previewSide === 'B' ? 6 : 1}
                      isVideo={panelType.mediaType === 'video'}
                    />
                  </div>
                </>
              )}

              <div className="mt-6 text-center text-xs text-slate-500">
                {isPoteau ? (
                  <>{affichesRemplies}/25 affiches poteaux · Avenue : <b>{selectedEmp?.nom}</b> · Format : <b>1,60 m × 1,20 m</b> par affiche</>
                ) : (
                  <>{affichesRemplies}/{affichesTotal} {mediaLabel}s{panelType.mediaType !== 'video' && <> · Format : <b>1,60 m × 1,20 m</b> par affiche · Total côté : <b>8 m × 1,20 m</b></>}</>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="border-t border-slate-200 bg-white px-5 py-3 flex items-center justify-between">
          <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="gap-2" data-testid="af-back">
            <ArrowLeft className="h-4 w-4" /> {step > 1 ? 'Précédent' : 'Annuler'}
          </Button>
          <div className="text-xs text-slate-400">
            {step === 1 && !panelType && 'Choisissez un type de panneau'}
            {step === 2 && !selectedEmp && 'Choisissez un panneau'}
            {step === 3 && `${affichesRemplies}/${affichesTotal} ${mediaLabel}s ajouté(e)s`}
          </div>
          {step < 4 ? (
            <div className="flex items-center gap-2">
              {step === 3 && (
                <Button variant="outline" onClick={() => saveCampaign()} disabled={savingCampaign} className="gap-2 border-blue-300 text-blue-700" data-testid="af-save-step3">
                  {savingCampaign ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Enregistrer
                </Button>
              )}
              <Button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !panelType) || (step === 2 && !selectedEmp)}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="af-next"
              >
                Suivant <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => saveCampaign()} disabled={savingCampaign} className="gap-2 border-blue-300 text-blue-700" data-testid="af-save">
                {savingCampaign ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Enregistrer
              </Button>
              <TechniqueButton onClick={sendToTechnique} sending={sendingTechnique} sent={techniqueSent} disabled={affichesRemplies === 0} testid="af-send-technique" />
              <Button
                onClick={generatePdf}
                disabled={generating || affichesRemplies === 0}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="af-generate"
              >
                <FileDown className="h-4 w-4" /> {generating ? 'Génération…' : 'Obtenir en PDF'}
              </Button>
            </div>
          )}
        </div>

        {/* Success dialog */}
        {showSuccess && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur flex flex-col items-center justify-center z-10">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-extrabold text-slate-900 mb-2">PDF généré avec succès !</h3>
            <div className="text-sm text-slate-600 mb-6 text-center">
              Votre fichier contient :
              <ul className="mt-2 space-y-1">
                <li>• {isDoubleSide ? 'Côté A' : 'Total'} : {coteA.filter(x => x?.path).length} {mediaLabel}{coteA.filter(x => x?.path).length > 1 ? 's' : ''}{isDoubleSide ? ' (recto)' : ''}</li>
                {isDoubleSide && <li>• Côté B : {coteB.filter(x => x?.path).length} affiche{coteB.filter(x => x?.path).length > 1 ? 's' : ''} (verso)</li>}
              </ul>
            </div>
            {(sentTo || techniqueSent) && (
              <div className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2" data-testid="af-sent-info">
                {sentTo ? 'Envoyé dans la messagerie de' : 'Services techniques notifiés'} : <b>{(sentTo || techniqueSent).join(', ')}</b>
              </div>
            )}
            <div className="flex gap-2 flex-wrap justify-center">
              <TechniqueButton onClick={sendToTechnique} sending={sendingTechnique} sent={techniqueSent} testid="af-success-send-technique" />
              <Button onClick={downloadPdf} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="af-download">
                <FileDown className="h-4 w-4" /> Télécharger le PDF
              </Button>
              <Button onClick={sendToTechniciens} disabled={sendingTech || !!sentTo} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="af-send-techniciens">
                {sendingTech ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sentTo ? 'Envoyé aux techniciens' : panelType?.mediaType === 'video' ? 'Envoyer au technicien pour diffusion' : 'Envoyer au technicien pour impression'}
              </Button>
              <Button variant="outline" onClick={onClose} data-testid="af-finish">
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TechniqueButton({ onClick, sending, sent, disabled, testid }) {
  return (
    <Button onClick={onClick} disabled={disabled || sending || !!sent} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid={testid}>
      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : sent ? <CheckCircle2 className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}
      {sent ? 'Envoyé aux services techniques' : 'Envoyer les images aux services techniques'}
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Side editor : upload de 5 affiches empilées
// ═══════════════════════════════════════════════════════════════════
function SideEditor({ side, label, slots, startNum, images, uploading, onUpload, onRemove, onSetCompany, accept = 'image/*', mediaType = 'image' }) {
  const isVideo = mediaType === 'video';
  return (
    <div>
      <div className="text-sm font-bold text-slate-900 mb-1">{label}</div>
      <div className="text-xs text-slate-500 mb-3">Ajoutez vos {isVideo ? 'vidéos' : 'images'} (de haut en bas)</div>
      <div className="flex gap-4">
        {/* Colonne empilée à gauche */}
        <div className="flex flex-col items-center">
          <div className="text-[10px] font-bold text-slate-500 mb-1">{slots === 5 ? '8,00 m' : slots + ' slots'}</div>
          <div className={`w-24 border-2 border-slate-300 rounded overflow-hidden ${isVideo ? 'bg-slate-900' : 'bg-slate-100'}`}>
            {Array.from({ length: slots }).map((_, i) => (
              <div key={i} className="border-b border-slate-300 last:border-b-0 aspect-[3/4] relative flex items-center justify-center bg-white">
                {images[i]?.previewUrl ? (
                  isVideo ? (
                    <video src={images[i].previewUrl} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={images[i].previewUrl} alt={`Slot ${startNum + i}`} className="w-full h-full object-cover" />
                  )
                ) : (
                  <div className="text-center text-slate-400">
                    <div className="text-lg font-bold">{startNum + i}</div>
                    <div className="text-[8px] leading-tight">{isVideo ? 'Ajouter\nune vidéo' : 'Ajouter\nune image'}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">1,20 m</div>
        </div>

        {/* Actions de chaque slot */}
        <div className="flex-1 space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {Array.from({ length: slots }).map((_, i) => {
            const uploadKey = `${side}-${i}`;
            const isUploading = uploading[uploadKey];
            const img = images[i];
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-slate-700 w-20 flex-shrink-0">{isVideo ? 'Vidéo' : 'Affiche'} {startNum + i}</div>
                  <div className="text-[10px] text-slate-500 w-20 flex-shrink-0">(1,60 × 1,20 m)</div>
                  {img?.path ? (
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="text-xs text-emerald-700 font-medium truncate">
                        {isVideo ? '🎬' : '✓'} {img.name || (isVideo ? 'vidéo' : 'image')}
                      </span>
                      <button onClick={() => onRemove(i)} className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0" data-testid={`af-remove-${side}-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex-1">
                      <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        disabled={isUploading}
                        onChange={(e) => e.target.files?.[0] && onUpload(i, e.target.files[0])}
                        data-testid={`af-upload-${side}-${i}`}
                      />
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-semibold cursor-pointer transition ${isUploading ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                        <Upload className="h-3 w-3" /> {isUploading ? 'Envoi…' : (isVideo ? 'Ajouter une vidéo' : 'Ajouter une image')}
                      </span>
                    </label>
                  )}
                </div>
                {/* Nom de l'entreprise annonceur */}
                <div className="flex items-center gap-2 pl-20">
                  <span className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">Entreprise</span>
                  <input
                    type="text"
                    value={img?.company || ''}
                    onChange={(e) => onSetCompany && onSetCompany(i, e.target.value)}
                    placeholder="Nom de l'entreprise annonceur"
                    className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                    data-testid={`af-company-${side}-${i}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Preview : rendu final vertical des 5 affiches
// ═══════════════════════════════════════════════════════════════════
function SidePreview({ images, startNum, isVideo = false }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-[10px] font-bold text-slate-500 mb-1">{images.length === 5 ? '8,00 m (5 × 1,60 m)' : `${images.length} ${isVideo ? 'vidéos' : 'affiches'}`}</div>
      <div className="w-48 border-2 border-slate-900 rounded overflow-hidden shadow-2xl">
        {images.map((img, i) => (
          <div key={i} className="border-b border-slate-800 last:border-b-0 aspect-[3/4] relative flex items-center justify-center bg-slate-100">
            {img?.previewUrl ? (
              isVideo
                ? <video src={img.previewUrl} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                : <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-slate-400">
                <div className="text-2xl font-bold">{startNum + i}</div>
                <div className="text-[9px]">{isVideo ? 'Aucune vidéo' : 'Aucune image'}</div>
              </div>
            )}
            <div className="absolute top-1 left-1 bg-slate-900/70 text-white text-[10px] font-bold px-1.5 rounded">
              {startNum + i}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-500 mt-1">1,20 m</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Poteau editor — grille 5×5 pour 25 affiches (une par poteau)
// ═══════════════════════════════════════════════════════════════════
function PoteauEditor({ images, uploading, onUpload, onRemove, onSetCompany, avenueName }) {
  return (
    <div>
      <div className="text-sm font-bold text-slate-900 mb-1">
        Poteaux 1 à 25 — {avenueName || '(sélectionnez une avenue)'}
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Ajoutez une affiche pour chaque poteau (dans l'ordre numérique 1 → 25).
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 max-h-[520px] overflow-y-auto pr-1">
        {Array.from({ length: 25 }).map((_, i) => {
          const uploadKey = `A-${i}`;
          const isUploading = uploading[uploadKey];
          const img = images[i];
          return (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-bold text-blue-700">Poteau n°{String(i + 1).padStart(2, '0')}</div>
                {img?.path && (
                  <button onClick={() => onRemove(i)} className="p-0.5 text-red-500 hover:bg-red-50 rounded" data-testid={`af-remove-poteau-${i}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="aspect-[3/4] w-full bg-slate-50 border border-slate-200 rounded overflow-hidden flex items-center justify-center">
                {img?.previewUrl ? (
                  <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-slate-400 text-[10px]">
                    <div className="text-lg font-bold">{i + 1}</div>
                    Aucune affiche
                  </div>
                )}
              </div>
              {!img?.path && (
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                    onChange={(e) => e.target.files?.[0] && onUpload(i, e.target.files[0])}
                    data-testid={`af-upload-poteau-${i}`}
                  />
                  <span className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-semibold cursor-pointer transition ${isUploading ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                    <Upload className="h-3 w-3" /> {isUploading ? 'Envoi…' : 'Ajouter'}
                  </span>
                </label>
              )}
              <input
                type="text"
                value={img?.company || ''}
                onChange={(e) => onSetCompany && onSetCompany(i, e.target.value)}
                placeholder="Entreprise"
                className="w-full text-[10px] border border-slate-200 rounded px-1.5 py-0.5 bg-white focus:border-blue-400"
                data-testid={`af-company-poteau-${i}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Poteau preview — grille 5×5 des 25 affiches
// ═══════════════════════════════════════════════════════════════════
function PoteauPreview({ images, avenueName }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-xs font-bold text-slate-600 mb-2">
        Aperçu tournée poteaux — {avenueName}
      </div>
      <div className="grid grid-cols-5 gap-1 border-2 border-slate-900 p-1 rounded shadow-2xl bg-slate-50" style={{ maxWidth: 460 }}>
        {Array.from({ length: 25 }).map((_, i) => {
          const img = images[i];
          return (
            <div key={i} className="aspect-[3/4] relative flex items-center justify-center bg-white border border-slate-300 rounded overflow-hidden">
              {img?.previewUrl ? (
                <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-slate-400 text-[10px]">
                  <div className="text-sm font-bold">{i + 1}</div>
                </div>
              )}
              <div className="absolute top-0 left-0 bg-blue-900/80 text-white text-[8px] font-bold px-1 rounded-br">
                {i + 1}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
